import { LS_TURBO } from "../../constants/SessionKeys";

/**
 * Turbo: an extra-intelligence switch on the AI features (product creation by text, photos and voice, voice edit,
 * image revamp, batch revamp). Off by default, remembered per business on this device, cleared on logout.
 *
 * Pure state, no network: useTurbo loads availability (AiApiService.fetchTurboAvailability) and stores it here.
 * Request builders read the switch with turboField(businessId) when they send.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let availability = new Map<string, boolean>();
let enabled: Record<string, boolean> = load();
/** Bumped on logout so an availability answer that lands afterwards is ignored. */
let session = 0;

function load(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LS_TURBO);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persist(): void {
  try {
    localStorage.setItem(LS_TURBO, JSON.stringify(enabled));
  } catch {
    // Private mode or storage full: Turbo still works for this session.
  }
}

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeTurbo(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** What requests use: switched on AND confirmed available for this business. */
export function isTurboOn(businessId: string | null | undefined): boolean {
  if (!businessId) return false;
  return enabled[businessId] === true && availability.get(businessId) === true;
}

/** Only the remembered switch position (UI state); availability is not checked here, see isTurboOn. */
export function isTurboSwitchedOn(businessId: string | null | undefined): boolean {
  return !!businessId && enabled[businessId] === true;
}

export function setTurbo(businessId: string, on: boolean): void {
  enabled = { ...enabled };
  if (on) enabled[businessId] = true;
  else delete enabled[businessId];
  persist();
  emit();
}

/** Cached availability: undefined while unknown. */
export function turboAvailability(businessId: string | null | undefined): boolean | undefined {
  return businessId ? availability.get(businessId) : false;
}

export function currentTurboSession(): number {
  return session;
}

/** Stores a definitive availability answer, unless the session changed (logout) since it was asked. */
export function setTurboAvailability(businessId: string, available: boolean, askedInSession: number): void {
  if (askedInSession !== session) return;
  availability.set(businessId, available);
  emit();
}

/** Logout: forget availability and switch positions, and ignore answers still in flight. */
export function resetTurboSession(): void {
  session += 1;
  availability = new Map();
  enabled = {};
  try {
    localStorage.removeItem(LS_TURBO);
  } catch {
    // nothing stored
  }
  emit();
}

/** For a request body: `{ turbo: true }` when Turbo is on for the business, otherwise nothing. */
export function turboField(businessId: string | null | undefined): { turbo?: true } {
  return isTurboOn(businessId) ? { turbo: true } : {};
}
